import {
	OUTRO,
	PRODUCT_DIFFERED,
	REQUISITES,
	SECTIONS,
	TEST_TECH,
	TEST_WEBCAM,
} from '../utils/constants';
import mic from '../img/icon-mic.png';
import bulb from '../img/icon-bulb.png';
import videoIcon from '../img/icon-video.png';
import mail from '../img/icon-mail.png';
import { useTranslation } from './useTranslation';
import { useMemo } from 'react';

/**
 * Useful to create Bubble on Waiting Room and Explain template. The Navbar component uses it too.
 * @param {boolean} hasVideoAnswers
 * @param {PRODUCT_DIFFERED | PRODUCT_LANGUAGE_ASSESSMENT | PRODUCT_TESTBUILDER} product
 * @param {number} globalTimer
 * @param {boolean} video - if the campaign have video answers
 * @param {boolean} hasRequisites
 * @param {boolean} hasProctoring
 * @return {({icon: {img, type: string}, video: boolean, proctoring: boolean, label: string, type: string, title: string, content: string}|{icon: {img, type: string}, video: boolean, label: string, type: string, title: string, content: string[]}|{icon: {img, type: string}, label: (string), type: string, title: string, content: unknown}|{stepToCount: boolean, icon: {img, type: string}, label: string, type: string, title: string, content: string[]}|{stepToCount: boolean, icon: {img, type: string}, label: string, type: string, title: string, content: string[]})[]}
 */
export const useStepsExplain = ({
	hasVideoAnswers,
	product,
	globalTimer,
	video,
	hasRequisites,
	hasProctoring,
}) => {
	const { t } = useTranslation();
	const content = useMemo(() => {
		if (hasVideoAnswers) {
			return `${t(
				'The questions prepared by the recruiter are asked one at a time. You must respond to them in a limited time, like in the practice session'
			)}.`;
		}
		if (globalTimer && !hasVideoAnswers) {
			return `${t(
				'The questions prepared by the recruiter are asked one at a time. You must respond to them in a limited time'
			)}.`;
		}
		if (!globalTimer && !hasVideoAnswers) {
			return t('The questions prepared by your assessor are asked one at a time.');
		}
		return null;
	}, [t, globalTimer, hasVideoAnswers]);

	const steps = useMemo(
		() => [
			{
				type: TEST_TECH,
				icon: { img: mic, type: 'mic' },
				video: true,
				proctoring: true,
				label: 'Vérifications',
				content: video
					? t(
							'The compatibility of your browser, the speed of your connection and the proper operation of your webcam and your microphone are checked'
					  )
					: t(
							'We will check your browser compatibility and connection speed and verify that your webcam is working correctly.'
					  ),
				title: t('Technical tests'),
			},
			{
				type: TEST_WEBCAM,
				icon: { img: bulb, type: 'bulb' },
				video: true,
				label: 'Entraînement',
				title: t('Familiarisation and practice'),
				content: [
					t('Learn about the video interview platform in the practice area.'),
					t('Your answers will not be kept, so feel free to try again!'),
				],
			},
			{
				type: SECTIONS,
				label: product === PRODUCT_DIFFERED ? 'Enregistrement' : 'Test',
				icon: { img: videoIcon, type: 'video' },
				title: !hasVideoAnswers ? t('Record your responses') : t('Online test'),
				content,
			},
			{
				stepToCount: true,
				label: 'Finalisation',
				type: REQUISITES,
				icon: { img: mail, type: 'mail' },
				title: t('Submit your documents'),
				content: [
					t('Please provide the following documents:'),
					t('Your answers are then made available to your assessor in a secure area.'),
					t('They will take it into account to assess your level.'),
				],
			},
			{
				stepToCount: true,
				label: 'Finalisation',
				type: OUTRO,
				icon: { img: mail, type: 'mail' },
				title: t('Finalize your test'),
				content: [
					t('Your answers are then made available to your assessor in a secure area.'),
					t('They will take it into account to assess your level.'),
				],
			},
		],
		[video, t, product, hasVideoAnswers, content]
	);

	const filterStepExplain = useMemo(
		() => steps.filter((step) => (hasRequisites ? step.type !== OUTRO : step.type !== REQUISITES)),
		[hasRequisites, steps]
	);

	return useMemo(
		() =>
			hasVideoAnswers
				? filterStepExplain
				: filterStepExplain.filter((step) => !step.video || (hasProctoring && step.proctoring)),
		[filterStepExplain, hasProctoring, hasVideoAnswers]
	);
};

export default useStepsExplain;
